import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { Delete, OpenInNew } from '@mui/icons-material';
import {
  data as docStatus,
  DOCENC,
  DOCVAL
} from '@lba-dev/package.local-globals/docStatus';
import { DOCART, DOCEXT, DOCINT } from '@lba-dev/package.local-globals/docFrom';
import { DRCPRO, DDECE, DCERT } from '@lba-dev/package.local-globals/docTypes';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import { withStyles } from 'tss-react/mui';
import { grey, brown } from '@mui/material/colors';

import stopPropagation from '../../utils/stopPropagation';
import ValidationButton from './ValidationButton';
import InsuranceValidationDetails from './InsuranceValidationDetails';
import InsuranceMetadataDisplay from './InsuranceMetadataDisplay';
import notifSystem from '../../notifSystem';

const styles = {
  root: {
    display: 'block',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '16px 20px',
    margin: '20px 5px',
    minHeight: '70px',
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 6px,' +
    'rgba(0, 0, 0, 0.23) 0px 3px 6px',
    borderLeft: '15px solid',
  },
  userSection: {
    flex: '0 0 150px',
    paddingRight: '16px'
  },
  userText: {
    color: '#6c757d',
    fontSize: '0.875rem'
  },
  fileSection: {
    flex: '1',
    paddingRight: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer'
  },
  fileName: {
    fontWeight: 600,
    color: '#2c3e50',
    textDecoration: 'none',
    textAlign: 'center',
    '&:hover': {
      textDecoration: 'underline',
      color: '#3498db'
    }
  },
  statusSection: {
    flex: '0 0 120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px'
  },
  chip: {
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '8px 16px',
    borderRadius: '20px',
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  actions: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center'
  },
  iconButton: {
    width: 32,
    height: 32,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem'
    }
  },
  aiMetadataContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    padding: '16px 20px',
    marginTop: '8px',
    marginLeft: '5px',
    marginRight: '5px',
    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 4px'
  }
};

const getStatusColor = ( status, isExpired ) => {
  if (isExpired) {
    return grey[500];
  }

  if (!status) {
    return brown[500];
  }
  const statusObj = docStatus.find(e => status === e._id);
  return statusObj ? statusObj.color[500] : brown[500];
};

const getStatusLabel = ( status, isExpired ) => {
  if (isExpired) {
    return 'Expiré';
  }

  if (!status) {
    return 'Non défini';
  }
  const statusObj = docStatus.find(e => status === e._id);
  return statusObj ? statusObj.name : 'Inconnu';
};

const getDocSource = (addedBy, userId, users = []) => {
  let docSource = 'Document ajouté par\n';
  if (addedBy === DOCART) {
    docSource += 'l\'artisan';
  } else if (addedBy === DOCINT && userId) {
    const user = users.find(u => u._id === userId);
    docSource += user ? user.login : `Utilisateur (${userId})`;
  } else if (addedBy === DOCEXT) {
    docSource += 'le client';
  } else {
    docSource += 'Inconnu';
  }
  return docSource;
};

const DocsRowItem = ({
  name,
  content,
  selected,
  handleClick,
  handleDelete,
  status,
  isExpired,
  sendStatus,
  requestAnalysis,
  addedBy,
  userId,
  users = [],
  classes,
  type,
  metadata,
  analysisError,
  endDate,
  date
}) => {
  const [loading, setLoading] = useState(false);
  const [modifiedEndDate, setModifiedEndDate] = useState(endDate || null);

  // Extraire la date de l'IA depuis metadata
  const aiEndDate = React.useMemo(
    () => metadata?.find(item => item.champ === 'endDate')?.value || null,
    [metadata]
  );

  // Vérifier si la date a été modifiée manuellement (différence > 1 jour)
  const isManuallyModified = React.useMemo(() => {
    if (!endDate) {
      return false;
    }
    if (endDate && !aiEndDate) {
      return true;
    }
    const diffInDays = Math.abs(dayjs(endDate).diff(aiEndDate, 'day'));
    return diffInDays > 1;
  }, [endDate, aiEndDate]);

  // Synchroniser modifiedEndDate quand endDate change
  useEffect(() => {
    setModifiedEndDate(endDate || null);
  }, [endDate]);
  const statusColor = getStatusColor(status, isExpired);
  const statusLabel = getStatusLabel(status, isExpired);
  const docSource = getDocSource(addedBy, userId, users);

  const handleFileClick = (e) => {
    e.stopPropagation();
    handleClick();
  };

  const handleOpenExternal = (e) => {
    e.stopPropagation();
    window.open(content, '_blank');
  };

  const handleSendStatus = (fileName, status, docType) => {
    if (
      [DRCPRO, DDECE].includes(docType) &&
      status === DOCVAL &&
      !endDate &&
      !modifiedEndDate
    ) {
      notifSystem.error(
        'Erreur',
        'La date de fin est requise pour envoyer le statut'
      );
      return;
    }
    setLoading(true);
    const dateToSend = modifiedEndDate !== endDate
      ? modifiedEndDate
      : null;
    sendStatus({
      fileName,
      status,
      endDate: dateToSend
    }, () => setLoading(false));
  };

  const handleEndDateChange = (newDate) => setModifiedEndDate(newDate);

  const handleRequestAnalysis = (fileName) => {
    setLoading(true);
    requestAnalysis(fileName, () => setLoading(false));
  };

  return (
    <Box
      className={classes.root}
      style={{
        borderLeftColor: statusColor,
        position: 'relative',
        opacity: loading ? 0.6 : 1,
        pointerEvents: loading ? 'none' : 'auto'
      }}
    >
      {loading && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}
      <Box display="flex" alignItems="center" m={5}>
        <Box
          className={classes.userSection}
        >
          <Typography
            className={classes.userText}
            variant="body1"
          >
            {docSource}
          </Typography>
          {date && (
            <Typography
              className={classes.userText}
              variant="body2"
            >
              {`le ${dayjs(date).format('DD/MM/YYYY à HH:mm')}`}
            </Typography>
          )}
        </Box>

        <Box
          className={classes.fileSection}
          onClick={handleFileClick}
        >
          <Typography
            className={classes.fileName}
            variant="body2"
            title={name}
          >
            {name?.length > 60 ? `${name.substring(0, 60)}...` : name}
          </Typography>
        </Box>
        <Box
          className={classes.statusSection}
        >
          <Chip
            className={classes.chip}
            label={statusLabel}
            style={{ backgroundColor: statusColor }}
            size="medium"
          />
          <Box
            className={classes.actions}
          >

            <IconButton
              className={classes.iconButton}
              onClick={stopPropagation(handleOpenExternal)}
              title="Ouvrir dans un nouvel onglet"
              size="small"
            >
              <OpenInNew />
            </IconButton>

            {handleDelete && (
              <IconButton
                className={classes.iconButton}
                onClick={stopPropagation(handleDelete)}
                title="Supprimer"
                size="small"
                color={selected ? 'secondary' : 'default'}
              >
                <Delete />
              </IconButton>
            )}

            {status === DOCENC && (
              <ValidationButton
                docType={type}
                fileName={name}
                sendStatus={handleSendStatus}
              />
            )}
          </Box>
        </Box>
      </Box>

      {(type === DRCPRO || type === DDECE || type === DCERT) && (
        <Box className={classes.aiMetadataContainer}>
          {analysisError && (
            <Alert severity="warning" style={{ marginBottom: '8px' }}>
              {analysisError.message}
              {analysisError.date &&
                ` (${dayjs(analysisError.date).format('DD/MM/YYYY HH:mm')})`}
            </Alert>
          )}
          {metadata?.length ? (
            <>
              <InsuranceMetadataDisplay
                metadata={metadata}
                status={status}
                onEndDateChange={handleEndDateChange}
                currentEndDate={modifiedEndDate}
                isDateManuallyModified={isManuallyModified}
              />
              <InsuranceValidationDetails metadata={metadata} />
              <Box style={{ textAlign: 'center', marginTop: '8px' }}>
                <IconButton
                  color="primary"
                  onClick={stopPropagation(() =>
                    handleRequestAnalysis(name))}
                  disabled={loading}
                  title="Relancer l'analyse IA"
                >
                  <Typography variant="button">Réanalyser</Typography>
                </IconButton>
              </Box>
            </>
          ) : (
            <Box style={{ padding: '16px', textAlign: 'center' }}>
              {!analysisError && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Ce document n'a pas encore été analysé par l'IA
                </Typography>
              )}
              <IconButton
                color="primary"
                onClick={stopPropagation(() =>
                  handleRequestAnalysis(name))}
                disabled={loading}
                title={analysisError
                  ? 'Relancer l\'analyse IA'
                  : 'Demander une analyse IA'}
              >
                <Typography variant="button">
                  {analysisError ? 'Réanalyser' : 'Analyser'}
                </Typography>
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default withStyles(DocsRowItem, styles);
